import { Fragment } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { Menu, Transition } from "@headlessui/react";
import {
  BellIcon,
  DocumentChartBarIcon,
  DocumentIcon,
} from "@heroicons/react/24/outline";

import { FETCH_REPORT_JOBS } from "../../../../graphql/query/ReportJob";
import ReportJobSummary from "../../../../modules/Reporting/components/ReportJobSummary";
import {
  formatReportJobProgress,
  reportJobHighlightPath,
  reportJobSection,
  reportSectionMeta,
  REPORT_JOB_STATUS_LABELS,
  REPORT_SECTION_META,
  REPORT_SECTIONS,
} from "../../../../modules/Reporting/utils";

const POLL_INTERVAL_MS = 10000;

// As many thumbnails as the dropdown's width takes without wrapping; the rest of
// the cohort collapses into the "+N more" beside them.
const NOTIFICATION_THUMBNAIL_LIMIT = 4;

/**
 * Which icon marks a job in the dropdown. Brochures get a plain document (they
 * are the PDF Generator's image-led PDFs), tabular reports get the chart
 * document — so a glance at the list tells you which section a job will open.
 */
const SECTION_ICONS = {
  [REPORT_SECTIONS.PDF_GENERATOR]: DocumentIcon,
  [REPORT_SECTIONS.REPORTING]: DocumentChartBarIcon,
};

/**
 * Header bell dropdown listing the user's in-flight (queued / processing)
 * report jobs across both sections. Clicking a job goes to whichever section
 * owns it with that job highlighted; the badge shows how many are in flight.
 */
const ReportNotifications = ({ buttonClassName, panelClassName }) => {
  const { data } = useQuery(FETCH_REPORT_JOBS, {
    variables: {
      input: { status: ["pending", "queued", "processing"], limit: 20 },
    },
    fetchPolicy: "cache-and-network",
    pollInterval: POLL_INTERVAL_MS,
  });

  const activeJobs = data?.reportJobs?.edges || [];

  return (
    <Menu as="div" className="relative mr-3">
      <Menu.Button className={buttonClassName} title="Reports in progress">
        <span className="sr-only">View report notifications</span>
        <span className="relative block">
          <BellIcon className="h-6 w-6" aria-hidden="true" />
          {activeJobs.length > 0 && (
            <span className="absolute -right-1.5 -top-1.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-blue-600 px-1 text-[10px] font-semibold leading-none text-white">
              {activeJobs.length}
            </span>
          )}
        </span>
      </Menu.Button>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-200"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items
          className={`${panelClassName} max-h-[calc(100dvh-5rem)] w-[calc(100vw-1rem)] max-w-[18rem] overflow-y-auto`}
        >
          {activeJobs.length === 0 ? (
            <div className="px-3 py-2 text-xs text-gray-500">
              No reports in progress
            </div>
          ) : (
            activeJobs.map(job => {
              const section = reportJobSection(job);
              const sectionMeta = reportSectionMeta(section);
              const SectionIcon = SECTION_ICONS[section] || DocumentChartBarIcon;
              return (
                <Menu.Item key={job.id}>
                  {({ active }) => (
                    <Link
                      to={reportJobHighlightPath(job)}
                      className={`flex items-start gap-2 px-3 py-2 text-xs text-gray-700 ${
                        active ? "bg-gray-100" : ""
                      }`}
                    >
                      <SectionIcon
                        className="mt-0.5 h-4 w-4 shrink-0 text-gray-400"
                        aria-hidden="true"
                      />
                      <div className="min-w-0 flex-1">
                        <ReportJobSummary
                          job={job}
                          size="compact"
                          thumbnailLimit={NOTIFICATION_THUMBNAIL_LIMIT}
                        />
                        <div className="mt-0.5 text-gray-500">
                          {sectionMeta.label} —{" "}
                          {REPORT_JOB_STATUS_LABELS[job?.status] || job?.status}
                          {formatReportJobProgress(job)
                            ? ` — ${formatReportJobProgress(job)}`
                            : ""}
                        </div>
                      </div>
                    </Link>
                  )}
                </Menu.Item>
              );
            })
          )}
          <div className="border-t border-gray-100">
            {Object.entries(REPORT_SECTION_META).map(([section, meta]) => {
              const SectionIcon = SECTION_ICONS[section] || DocumentChartBarIcon;
              return (
                <Menu.Item key={section}>
                  {({ active }) => (
                    <Link
                      to={meta.path}
                      className={`flex items-center gap-2 px-3 py-2 text-xs font-medium text-blue-600 ${
                        active ? "bg-gray-100" : ""
                      }`}
                    >
                      <SectionIcon
                        className="h-4 w-4 shrink-0"
                        aria-hidden="true"
                      />
                      View all in {meta.label}
                    </Link>
                  )}
                </Menu.Item>
              );
            })}
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
};

export default ReportNotifications;
